import React, { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import RadioButton from '@wix/da-ds/pkg/RadioButton';
import InputErrorMessage from '@wix/da-ds/pkg/formControls/lego/InputErrorMessage';

import s from './OriginalsDeclineModal.scss';

interface Props {
  onDecline: (
    reason: string,
    onSuccess: () => void,
    onError: (error: Error) => void
  ) => void;
  onRequestClose: () => void;
}

const OriginalsDeclineModal: React.FC<Props> = ({
  onDecline,
  onRequestClose,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);

  const DECLINE_REASONS = [
    {
      label: t('studio.modals.originalsDecline.reasons.workload'),
      value: 'workload',
    },
    {
      label: t('studio.modals.originalsDecline.reasons.no_details'),
      value: 'no_details',
    },
    {
      label: t('studio.modals.originalsDecline.reasons.cannot_deliver'),
      value: 'cannot_deliver',
    },
    {
      label: t('studio.modals.originalsDecline.reasons.out_of_scope'),
      value: 'out_of_scope',
    },
    {
      label: t('studio.modals.originalsDecline.reasons.other'),
      value: 'other',
    },
  ];

  const onDeclineClick = useCallback(() => {
    if (!selectedReason) {
      setShowErrorMessage(true);
      setIsBusy(false);
      return;
    }

    setIsBusy(true);
    onDecline(selectedReason, onRequestClose, onRequestClose);
  }, [onDecline, onRequestClose, selectedReason]);

  return (
    <Modal
      className={s['root']}
      layoutClassName={s['layout']}
      contentClassName={s['content']}
      heading={t('studio.modals.originalsDecline.title')}
      subheading={t('studio.modals.originalsDecline.subheading')}
      footerRight={[
        <TextButton
          key="cancel"
          className={s['button']}
          onClick={onRequestClose}
          size="medium"
          variant="main"
          disabled={isBusy}
        >
          {t('common.cancel')}
        </TextButton>,
        <SolidButton
          key="decline"
          className={s['button']}
          onClick={onDeclineClick}
          size="medium"
          variant="destructive-red"
          disabled={isBusy}
        >
          {t('studio.modals.originalsDecline.actions.decline')}
        </SolidButton>,
      ]}
      onRequestClose={onRequestClose}
      showHeaderBorder
      showFooterBorder
      shouldCloseOnOverlayClick={false}
      isOpen
      isFullscreen={isMobile}
      withScrollableContentWrapper
    >
      {DECLINE_REASONS.map(({ label, value }) => (
        <RadioButton
          key={value}
          className={s['reason']}
          name="reason"
          value={value}
          checked={selectedReason === value}
          onChange={e => setSelectedReason(e.target.value)}
        >
          {label}
        </RadioButton>
      ))}
      {showErrorMessage && (
        <InputErrorMessage className={s['error-message']}>
          {t('studio.modals.originalsDecline.errorMessage')}
        </InputErrorMessage>
      )}
    </Modal>
  );
};
OriginalsDeclineModal.displayName = 'OriginalsDeclineModal';

export default OriginalsDeclineModal;
